import {
  EMPTY_OBJECT,
  HamburgerMenu,
  filterByCellValueMenuItem,
  useJsonModal,
  useMixpanel,
  type AgGridMenuItems,
  type Column,
  type ColumnDef,
  type Exposure,
  type FilterableProperty,
  type Quote,
  type UseFilterBuilderOutput,
} from '@talos/kyoko';
import type { GetContextMenuItemsParams } from 'ag-grid-community';
import { compact, isNil } from 'lodash-es';
import { useCallback, useMemo, type ReactNode } from 'react';
import { useQuoteContextMenuActions } from './MenuItems/useQuoteContextMenuActions';
import { colIDToFilterBuilderKey } from './useQuotesFilter';

export function useQuotesMenu({
  openClause,
  filterableProperties,
}: {
  openClause?: UseFilterBuilderOutput['addAndOpenClause'];
  filterableProperties?: FilterableProperty<string>[];
} = EMPTY_OBJECT): {
  columns: Column[];
  getCustomContextMenuItems: (params: GetContextMenuItemsParams<Quote>) => AgGridMenuItems;
  dialogs: ReactNode;
} {
  const mixpanel = useMixpanel();
  const { handleClickJson, jsonModal } = useJsonModal();
  const { getQuoteActionMenuItems, dialog: quoteActionDialog } = useQuoteContextMenuActions();

  const getCustomContextMenuItems = useCallback(
    (params: GetContextMenuItemsParams<Quote>) => {
      const quote = params?.node?.data;
      if (isNil(quote)) {
        return [];
      }

      let menuItems: AgGridMenuItems = [];

      if (filterableProperties != null && openClause != null) {
        menuItems = menuItems.concat(
          filterByCellValueMenuItem({
            params,
            filterableProperties,
            openClause,
            colIDToFilterBuilderKey,
            mixpanel,
          })
        );
      }

      return compact([...menuItems, ...getQuoteActionMenuItems(quote)]);
    },
    [filterableProperties, openClause, mixpanel, getQuoteActionMenuItems]
  );

  const dialogs = useMemo(
    () => (
      <>
        {jsonModal}
        {quoteActionDialog}
      </>
    ),
    [jsonModal, quoteActionDialog]
  );

  const columns = useMemo(
    () =>
      compact([
        {
          type: 'hamburgerMenu',
          id: 'rowMenu',
          params: {
            renderItems: params => <HamburgerMenu {...params} onShowJson={handleClickJson} />,
          },
        },
      ] satisfies ColumnDef<Exposure>[]),
    [handleClickJson]
  );

  return {
    columns,
    getCustomContextMenuItems,
    dialogs,
  };
}
